import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ResolvedMunicipality } from '@buzzmap/shared';
import { useDiscoveryOptional } from '../../contexts/discoveryStore';
import styles from './HintSelectionPrompt.module.css';

/**
 * Legacy-hint ambiguity prompt (T013-FE, plan l.61/l.147).
 *
 * A generic entry whose geographic hint matches more than one current comune
 * (`hintStatus: 'needs-selection'` — e.g. bare `/esplora?city=San%20Teodoro`
 * without a province) is HELD by `DiscoveryProvider`: the feed never queries,
 * and the provider never picks the first homonym. This dialog renders the
 * candidate list so the visitor chooses; picking one replace-navigates to its
 * canonical path (the entry mechanism then resolves it like any other
 * territorial URL, and the prompt disappears with the hint state).
 *
 * Accessibility (review-T013 W-1): the dialog contains focus — Tab/Shift+Tab
 * cycle among its candidates and Escape is swallowed (deliberately NO
 * dismiss, see {@link handleDialogKeyDown}). Focus is restored to the element
 * that had it on open when the dialog closes (same pattern as
 * FilterPopup/date-review).
 *
 * Rendered only by `DiscoveryProvider` (public tree, v2 mode) — the organizer
 * console never mounts it.
 */

/**
 * Keyboard policy of the open dialog (review-T013 W-1):
 * - Escape is swallowed: deliberate NO dismiss — an unresolved hint must not
 *   fall back to broader results (plan l.61), and a stray keypress must
 *   never silently pick a homonym (l.147).
 * - Tab/Shift+Tab cycle among the dialog's focusables, so keyboard users
 *   cannot tab out onto the overlaid background (`aria-modal` alone does not
 *   contain focus).
 */
function handleDialogKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
  if (e.key === 'Escape') {
    e.preventDefault();
    return;
  }
  if (e.key !== 'Tab') return;
  const dialog = e.currentTarget;
  const focusables = Array.from(
    dialog.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute('disabled'));
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (!first || !last) return;
  const active = document.activeElement as HTMLElement | null;
  const inside = active !== null && dialog.contains(active);
  if (e.shiftKey) {
    if (!inside || active === first) {
      e.preventDefault();
      last.focus();
    }
  } else if (!inside || active === last) {
    e.preventDefault();
    first.focus();
  }
}

export function HintSelectionPrompt() {
  const discovery = useDiscoveryOptional();
  const navigate = useNavigate();
  const firstCandidateRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Only candidates the registry could path (plan l.61): a row without a
  // territorial path has nowhere to navigate to.
  const candidates =
    discovery?.mode === 'v2' && discovery.hintStatus === 'needs-selection'
      ? discovery.hintCandidates.filter(
          (c): c is ResolvedMunicipality & { path: string } => Boolean(c.path)
        )
      : [];
  const open = candidates.length > 0;

  // Focus lifecycle: capture who had focus, move it into the dialog, restore
  // on close (FilterPopup pattern; no enter animation, so no focus-in delay).
  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    firstCandidateRef.current?.focus();
    return () => {
      const previous = previousFocusRef.current;
      previousFocusRef.current = null;
      if (previous && document.contains(previous)) previous.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} data-testid="hint-selection-prompt">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="hint-selection-title"
        aria-describedby="hint-selection-desc"
        className={styles.dialog}
        onKeyDown={handleDialogKeyDown}
      >
        <h2 id="hint-selection-title" className={styles.title}>
          Quale comune intendi?
        </h2>
        <p id="hint-selection-desc" className={styles.description}>
          Ci sono più comuni con questo nome. Scegli quello di cui vuoi vedere gli eventi.
        </p>
        <ul className={styles.list}>
          {candidates.map((candidate, index) => (
            <li key={candidate.id}>
              <button
                type="button"
                ref={index === 0 ? firstCandidateRef : undefined}
                onClick={() => navigate(candidate.path, { replace: true })}
                className={styles.candidate}
              >
                {candidate.name}
                {candidate.province ? ` (${candidate.province})` : ''}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
